import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useAuthValidation } from '../hooks/useAuthValidation';
import { registrarTimbrado, buscarTimbrado } from '../services/api';
import { DateInput } from '../utils/dateUtils';

const TimbradoManager = () => {
  const [loading, setLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [formData, setFormData] = useState({
    ruc: '',
    numero_timbrado: '',
    fecha_inicio_vigencia: '',
    fecha_fin_vigencia: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!formData.ruc) {
      toast.error('Por favor ingrese el RUC para buscar');
      return;
    }

    const buscandoTodos = !formData.fecha_inicio_vigencia;

    setLoading(true);
    try {
      const result = await buscarTimbrado(formData.ruc, formData.fecha_inicio_vigencia);
      setSearchResult(result);

      if (result.encontrado) {
        toast.success(buscandoTodos ? 'Timbrados encontrados' : 'Timbrado encontrado');
      } else {
        toast.info(buscandoTodos ? 'No se encontraron timbrados para este RUC' : 'No se encontró timbrado para esta fecha');
      }
    } catch (error) {
      console.error('Error searching timbrado:', error);
      setSearchResult({ encontrado: false });
      toast.info(buscandoTodos ? 'No se encontraron timbrados para este RUC' : 'No se encontró timbrado para esta fecha');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.ruc || !formData.numero_timbrado || !formData.fecha_inicio_vigencia || !formData.fecha_fin_vigencia) {
      toast.error('Todos los campos son requeridos');
      return;
    }

    setLoading(true);
    try {
      await registrarTimbrado(formData);
      toast.success('Timbrado registrado correctamente');
      
      // Reset form
      setFormData({
        ruc: '',
        numero_timbrado: '',
        fecha_inicio_vigencia: '',
        fecha_fin_vigencia: '',
      });
      setSearchResult(null);
    } catch (error) {
      console.error('Error registering timbrado:', error);
      const errorMsg = error.response?.data?.error || 'Error al registrar timbrado';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="timbrado-manager">
      <div className="row">
        {/* Register Timbrado Form */}
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Registrar Nuevo Timbrado</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} data-testid="register-timbrado-form">
                <div className="mb-3">
                  <label className="form-label">RUC (sin DV) *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="ruc"
                    value={formData.ruc}
                    onChange={handleChange}
                    placeholder="Ej: 80012345"
                    data-testid="register-ruc-input"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Número de Timbrado *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="numero_timbrado"
                    value={formData.numero_timbrado}
                    onChange={handleChange}
                    placeholder="Ej: 12345678"
                    data-testid="register-numero-input"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Fecha Inicio Vigencia *</label>
                  <DateInput
                    name="fecha_inicio_vigencia"
                    value={formData.fecha_inicio_vigencia}
                    onChange={handleChange}
                    data-testid="register-fecha-inicio-input"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Fecha Fin Vigencia *</label>
                  <DateInput
                    name="fecha_fin_vigencia"
                    value={formData.fecha_fin_vigencia}
                    onChange={handleChange}
                    data-testid="register-fecha-fin-input"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={loading}
                  data-testid="register-submit-button"
                >
                  {loading ? 'Registrando...' : 'Registrar Timbrado'}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Search Timbrado Form */}
        <div className="col-md-6">
          <div className="card shadow-sm">
            <div className="card-header bg-info text-white">
              <h5 className="mb-0">Buscar Timbrado</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSearch} data-testid="search-timbrado-form">
                <div className="mb-3">
                  <label className="form-label">RUC (sin DV) *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="ruc"
                    value={formData.ruc}
                    onChange={handleChange}
                    placeholder="Ej: 80012345"
                    data-testid="search-ruc-input"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Fecha de Consulta</label>
                  <DateInput
                    name="fecha_inicio_vigencia"
                    value={formData.fecha_inicio_vigencia}
                    onChange={handleChange}
                    data-testid="search-fecha-input"
                  />
                  <div className="form-text">Déjelo vacío para ver todos los timbrados del RUC.</div>
                </div>

                <button
                  type="submit"
                  className="btn btn-info w-100"
                  disabled={loading}
                  data-testid="search-submit-button"
                >
                  {loading ? 'Buscando...' : 'Buscar Timbrado'}
                </button>
              </form>

              {searchResult && (
                <div
                  className={`alert ${searchResult.encontrado ? 'alert-success' : 'alert-warning'} mt-3`}
                  data-testid="search-result"
                >
                  {searchResult.timbrados ? (
                    searchResult.encontrado ? (
                      <>
                        <h6>Timbrados Encontrados</h6>
                        <ul className="mb-0 ps-3">
                          {searchResult.timbrados.map((t) => (
                            <li key={t.numero_timbrado}>
                              <strong>{t.numero_timbrado}</strong> — {t.nombre_razon_social} ({t.fecha_inicio} - {t.fecha_fin})
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <p className="mb-0">No se encontraron timbrados para este RUC</p>
                    )
                  ) : searchResult.encontrado ? (
                    <>
                      <h6>Timbrado Encontrado</h6>
                      <p className="mb-1"><strong>Número:</strong> {searchResult.numero_timbrado}</p>
                      <p className="mb-1"><strong>Inicio:</strong> {searchResult.fecha_inicio}</p>
                      <p className="mb-0"><strong>Fin:</strong> {searchResult.fecha_fin}</p>
                    </>
                  ) : (
                    <p className="mb-0">No se encontró timbrado activo para esta fecha</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimbradoManager;
